import { Subscription } from '../rpc'

export type GamepadSource =
  | 'web-gamepad' // desktop web / Electron renderer
  | 'steam-input' // Steamworks Steam Input (Deck + remapped pads)
  | 'ios-gamecontroller' // GCController (Backbone, MFi)
  | 'android-input' // InputDevice / KeyEvent / MotionEvent

export type GamepadButtonName =
  | 'a'
  | 'b'
  | 'x'
  | 'y'
  | 'leftBumper'
  | 'rightBumper'
  | 'leftTrigger'
  | 'rightTrigger'
  | 'select'
  | 'start'
  | 'guide'
  | 'leftStick'
  | 'rightStick'
  | 'dpadUp'
  | 'dpadDown'
  | 'dpadLeft'
  | 'dpadRight'

export interface GamepadButtonState {
  pressed: boolean
  /** Analog 0..1 (triggers); 0/1 for digital buttons. */
  value: number
}

export interface GamepadAxes {
  leftX: number
  leftY: number
  rightX: number
  rightY: number
}

export interface GamepadSnapshot {
  index: number
  id: string
  connected: boolean
  source: GamepadSource
  /** True when buttons map to the standard layout above; false = best-effort. */
  standardMapping: boolean
  buttons: Record<GamepadButtonName, GamepadButtonState>
  /** Each axis normalized to -1..1. */
  axes: GamepadAxes
  /** Monotonic ms (performance.now) when the provider sampled the device. */
  sourceTimestamp: number
  /**
   * Monotonic ms when the SDK cached/served this snapshot. Equals
   * `sourceTimestamp` on Tier 1 (direct read); larger by the transport cost on
   * Tier 2. `deliveredTimestamp - sourceTimestamp` is the added input latency.
   */
  deliveredTimestamp: number
}

export interface GamepadConnectionEvent {
  index: number
  id: string
  source: GamepadSource
}

export interface GamepadLatencyStats {
  p50: number
  p95: number
  max: number
  sampleCount: number
}

export interface GamepadApi {
  /**
   * Resolves once the one-time `GAMEPAD_SUBSCRIBE` handshake has settled (success,
   * timeout, or reject) — i.e. once `isSupported()` is authoritative. Never
   * rejects, never hangs. This is the readiness contract consumers MUST await
   * before trusting `isSupported()`: the handshake is async and the SDK fires it
   * during init, so `isSupported()` is routinely still `false` at game mount.
   * Without awaiting this, a consumer either gives up on a transient `false`
   * (race) or polls in hope it flips (silent fallback). On the local/mock tier
   * there is no handshake, so this resolves immediately.
   */
  ready(): Promise<void>
  /**
   * True when the SDK can read controllers on this surface (capability —
   * independent of whether a pad is currently connected). Determined solely by
   * the one-time `GAMEPAD_SUBSCRIBE` handshake: `true` iff the host returned a
   * valid `{ supported: true, mode: 'tier1' | 'tier2' }`. The SDK never infers
   * this from `navigator.getGamepads` (which exists yet is broken under Steam
   * Input). Returns `false` before the handshake resolves (await `ready()`
   * first) and on timeout/reject/`supported: false`/unknown `mode`. Never hangs.
   */
  isSupported(): boolean
  /**
   * Synchronous snapshot of currently-connected pads (empty if none; never
   * `null`/disconnected slots). Tier 1 reads `navigator.getGamepads()` live on
   * each call (filtering empty slots); Tier 2 returns the latest host-pushed
   * cache. Exactly one tier per instance; no RPC in either tier.
   */
  getGamepads(): GamepadSnapshot[]
  onConnected(callback: (event: GamepadConnectionEvent) => void): Subscription
  onDisconnected(callback: (event: GamepadConnectionEvent) => void): Subscription
  /** Performance telemetry, off unless the input debug flag is enabled. */
  readonly __debug: {
    getLatencyStats(): GamepadLatencyStats
  }
}

export const GAMEPAD_BUTTON_NAMES: readonly GamepadButtonName[] = [
  'a',
  'b',
  'x',
  'y',
  'leftBumper',
  'rightBumper',
  'leftTrigger',
  'rightTrigger',
  'select',
  'start',
  'guide',
  'leftStick',
  'rightStick',
  'dpadUp',
  'dpadDown',
  'dpadLeft',
  'dpadRight',
]
